import React, { useState, useEffect, useRef } from 'react';
import { lockScroll, unlockScroll } from '../utils/scrollbarUtils';
import { sendCallToPOS } from '../utils/unionApi';
import { sendCallToOKDC } from '../utils/okdcApi';
import { sendCallToMetacity } from '../utils/metacityApi';
import { sendStaffCallToToss } from '../utils/tossApi';
import { sendStaffCallToWowpos } from '../utils/wowposApi';
import { simApi } from '../utils/simApi';
import api from '../utils/api';
import bellIcon from '../assets/icons/common/bellicon.png';
import bottomNavBg from '../assets/icons/common/bottomNav.png';

const StaffCallBottomSheet = ({ isOpen, onClose, selectedLanguage = 'ko', onCall, tableCode = '1', storeId = null, posType = null, isSimulation = false }) => {
  // Translations
  const translations = {
    ko: {
      headerTitle: '도움이 필요하신가요?',
      close: '닫기',
      call: '호출',
      notSupportedLine1: '사용하고 계신 포스는',
      notSupportedLine2: '호출 기능을 제공하지 않아요 :('
    },
    en: {
      headerTitle: 'Need help?',
      close: 'Close',
      call: 'Call',
      notSupportedLine1: 'The POS system you are using',
      notSupportedLine2: 'does not support the call feature :('
    },
    ja: {
      headerTitle: 'お困りですか？',
      close: '閉じる',
      call: '呼出',
      notSupportedLine1: 'ご利用中のPOSは',
      notSupportedLine2: '呼出機能に対応していません :('
    },
    zh: {
      headerTitle: '需要帮助吗？',
      close: '关闭',
      call: '呼叫',
      notSupportedLine1: '您正在使用的POS',
      notSupportedLine2: '不支持呼叫功能 :('
    }
  };
  
  const t = translations[selectedLanguage] || translations.ko;
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [scrollbarTop, setScrollbarTop] = useState(0);
  const [scrollbarHeight, setScrollbarHeight] = useState(50);
  const [isScrollbarDragging, setIsScrollbarDragging] = useState(false);
  const bottomSheetRef = useRef(null);
  const wrapperRef = useRef(null);
  const optionsListRef = useRef(null);
  const scrollbarRef = useRef(null);

  // 호출 옵션 목록 - DB에서 로드 (초기값 비움)
  const [callOptions, setCallOptions] = useState([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);

  // DB에서 호출 사유 로드 - 항상 최신 데이터를 가져오도록 캐시 방지
  const loadCallReasons = async () => {
    try {
      setIsLoadingReasons(true);

      // 데모(체험) 모드: 고정 호출 사유 - 언어별 라벨 (id는 인덱스 기반으로 언어 무관 고정)
      if (posType === 'demo') {
        const demoReasons = {
          ko: ['직원호출', '물 주세요', '앞접시 주세요', '앞치마 주세요'],
          en: ['Call Staff', 'Water, please', 'Small plate, please', 'Apron, please'],
          ja: ['店員を呼ぶ', 'お水ください', '取り皿ください', 'エプロンください'],
          zh: ['呼叫服务员', '请给水', '请给餐盘', '请给围裙']
        };
        const R = demoReasons[selectedLanguage] || demoReasons.ko;
        setCallOptions(R.map((label, i) => ({ id: `demo-reason-${i}`, label })));
        return;
      }

      // storeId가 없으면 로드하지 않음 (localStorage 오염 방지)
      if (!storeId) {
        console.warn('[StaffCallBottomSheet] storeId가 없어 호출 사유를 로드할 수 없습니다.');
        setCallOptions([]);
        return;
      }

      // 브라우저 캐시 방지를 위한 추가 timestamp
      const response = await api.getCallReasonsWithStoreId(storeId, selectedLanguage);
      if (response.success && response.reasons) {
        // DB 응답을 기존 형식으로 변환
        const options = response.reasons.map((reason) => ({
          id: reason.id,
          label: reason.reason_text  // API에서 언어별로 필터링된 텍스트
        }));
        setCallOptions(options);
        console.log(`Loaded ${options.length} call reasons for language: ${selectedLanguage}`);
      }
    } catch (e) {
      console.error('Failed to load call reasons from DB:', e);
      // 에러 시 기본값 설정
      setCallOptions([]);
    } finally {
      setIsLoadingReasons(false);
    }
  };

  // 언어 변경 시에도 호출 사유 재로드 (storeId 변경 시에도)
  useEffect(() => {
    if (isOpen && storeId) {
      loadCallReasons();
    }
  }, [selectedLanguage, storeId]);

  // posType 디버깅
  useEffect(() => {
    if (isOpen) {
      console.log('[StaffCallBottomSheet] posType:', posType);
    }
  }, [isOpen, posType]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(false);
      setIsVisible(true);

      // body 스크롤 방지
      lockScroll();

      // DB에서 최신 호출 설정 로드 (비동기로 처리하여 UI 차단 방지)
      if (storeId) {
        setTimeout(() => {
          loadCallReasons();
        }, 0);
      }

      window.history.pushState({ staffCall: true }, '');

      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      setIsAnimating(false);
      
      setTimeout(() => {
        setIsVisible(false);
        setSelectedItems([]);
        // 애니메이션이 완료된 후 body 스크롤 복원
        unlockScroll();
      }, 200);
    }
  }, [isOpen]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (isOpen) {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  // 화면 크기에 따른 스케일 조정
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = wrapperRef.current;
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    if (isVisible) {
      scaleUI();
      window.addEventListener('resize', scaleUI);
      return () => {
        window.removeEventListener('resize', scaleUI);
      };
    }
  }, [isVisible]);

  // 스크롤바 계산 및 업데이트
  useEffect(() => {
    const updateScrollbar = () => {
      if (optionsListRef.current) {
        const scrollHeight = optionsListRef.current.scrollHeight;
        const clientHeight = optionsListRef.current.clientHeight;
        const scrollTop = optionsListRef.current.scrollTop;
        
        // 스크롤바가 필요한지 확인
        if (scrollHeight <= clientHeight) {
          setScrollbarTop(-100); // 스크롤바 숨기기
          return;
        }
        
        // 스크롤바 위치 계산 (60% 길이의 스크롤바)
        const scrollableHeight = scrollHeight - clientHeight;
        const scrollPercentage = scrollTop / scrollableHeight;
        const trackHeight = clientHeight - 100; // bottom-actions 높이 제외
        const calculatedScrollbarHeight = trackHeight * 0.6; // 스크롤바 높이를 트랙의 60%로 설정
        const maxPosition = trackHeight - calculatedScrollbarHeight;
        const scrollbarPosition = scrollPercentage * maxPosition;
        
        setScrollbarHeight(calculatedScrollbarHeight);
        setScrollbarTop(scrollbarPosition);
      }
    };

    const handleScroll = () => {
      if (!isScrollbarDragging) {
        updateScrollbar();
      }
    };

    const optionsList = optionsListRef.current;
    if (optionsList && isVisible) {
      updateScrollbar();
      optionsList.addEventListener('scroll', handleScroll);
      return () => {
        optionsList.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isVisible, isScrollbarDragging]);

  // 터치/마우스 이벤트 핸들러
  const handleStart = (clientY) => {
    setIsDragging(true);
    setDragStartY(clientY);
    setCurrentY(0);
  };

  const handleMove = (clientY) => {
    if (!isDragging) return;
    const deltaY = clientY - dragStartY;
    if (deltaY > 0) { // 아래로만 드래그 가능
      setCurrentY(deltaY);
    }
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // 화면 높이의 20% 이상 또는 150px 이상 드래그하면 닫기
    const threshold = Math.min(window.innerHeight * 0.2, 150);
    if (currentY > threshold) {
      // 부드럽게 닫기 애니메이션
      setCurrentY(window.innerHeight);
      setTimeout(() => {
        onClose();
        setCurrentY(0);
      }, 200);
    } else {
      // 원위치로 돌아가기
      setCurrentY(0);
    }
  };

  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientY);
  };
  
  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientY);
  };
  
  const handleTouchEnd = (e) => {
    handleEnd();
  };
  const handleMouseDown = (e) => handleStart(e.clientY);
  const handleMouseMove = (e) => handleMove(e.clientY);
  const handleMouseUp = () => handleEnd();

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMove(e.clientY);
      const handleGlobalMouseUp = () => handleEnd();
      const handleGlobalTouchMove = (e) => handleMove(e.touches[0].clientY);
      const handleGlobalTouchEnd = () => handleEnd();

      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      window.addEventListener('touchend', handleGlobalTouchEnd, { passive: false });
      
      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
        window.removeEventListener('touchmove', handleGlobalTouchMove);
        window.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
  }, [isDragging, dragStartY, currentY]);

  // 스크롤바 드래그 핸들러
  const handleScrollbarMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsScrollbarDragging(true);
    const startY = e.clientY || e.touches[0].clientY;
    const startTop = scrollbarTop;
    
    const handleScrollbarMove = (e) => {
      const currentY = e.clientY || e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      if (optionsListRef.current) {
        const clientHeight = optionsListRef.current.clientHeight;
        const scrollHeight = optionsListRef.current.scrollHeight;
        const trackHeight = clientHeight - 100; // bottom-actions 높이 제외
        const scrollbarHeight = trackHeight * 0.6; // 스크롤바 높이를 트랙의 60%로 설정
        const maxPosition = trackHeight - scrollbarHeight;
        
        // 새 스크롤바 위치 계산
        let newTop = startTop + deltaY;
        newTop = Math.max(0, Math.min(newTop, maxPosition));
        setScrollbarTop(newTop);
        
        // 스크롤 위치 계산
        const scrollPercentage = newTop / maxPosition;
        const newScrollTop = scrollPercentage * (scrollHeight - clientHeight);
        optionsListRef.current.scrollTop = newScrollTop;
      }
    };
    
    const handleScrollbarUp = () => {
      setIsScrollbarDragging(false);
      document.removeEventListener('mousemove', handleScrollbarMove);
      document.removeEventListener('mouseup', handleScrollbarUp);
      document.removeEventListener('touchmove', handleScrollbarMove);
      document.removeEventListener('touchend', handleScrollbarUp);
    };
    
    document.addEventListener('mousemove', handleScrollbarMove);
    document.addEventListener('mouseup', handleScrollbarUp);
    document.addEventListener('touchmove', handleScrollbarMove, { passive: false });
    document.addEventListener('touchend', handleScrollbarUp);
  };

  const toggleItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleCall = async () => {
    if (selectedItems.length > 0) {
      // 진동 피드백
      if (navigator.vibrate) {
        navigator.vibrate(30);
      }
      
      // 선택된 항목들의 라벨 텍스트 가져오기
      const selectedLabels = selectedItems.map(itemId => {
        const option = callOptions.find(opt => opt.id === itemId);
        return option ? option.label : '';
      }).filter(label => label);
      
      // 호출 텍스트 생성 (선택된 항목들을 쉼표로 구분)
      const callText = selectedLabels.join(', ');
      
      console.log('호출 항목:', callText);
      
      try {
        console.log('[직원호출] 사용할 테이블 코드:', tableCode, 'posType:', posType, 'sim:', isSimulation);
        // OKDC + Union + WowPos 모두 동일 패턴: 백엔드가 reasonIds → 한국어 변환 후 자체 WebSocket forward
        let result;
        if (posType === 'demo') {
          // 데모(체험): 백엔드 미호출, 성공 처리만
          result = { success: true };
        } else if (isSimulation) {
          // 시뮬레이션: 실 POS API 대신 simApi → 'simulation' room 브로드캐스트 (POS 화면에 호출 카드 표시)
          result = await simApi.sendStaffCall({ callItems: selectedLabels });
        } else if (posType === 'okdc') {
          result = await sendCallToOKDC(storeId, tableCode, selectedItems);
        } else if (posType === 'metacity') {
          result = await sendCallToMetacity(storeId, tableCode, selectedItems);
        } else if (posType === 'wowpos') {
          result = await sendStaffCallToWowpos({ storeId, tableName: tableCode, reasonIds: selectedItems });
        } else if (posType === 'toss') {
          // 토스: 매장 PC 클라이언트(토스용)가 호출 팝업을 띄운다 — 다른 POS 와 같은 구조
          result = await sendStaffCallToToss(storeId, tableCode, selectedItems);
        } else {
          result = await sendCallToPOS(storeId, tableCode, selectedItems);
        }

        if (result.success) {
          console.log('POS 호출 성공:', result);
        } else {
          console.error('POS 호출 실패:', result.error || result.message);
        }
      } catch (error) {
        console.error('POS 호출 중 오류:', error);
      }
      
      // 호출 완료 콜백 호출
      if (onCall) {
        onCall();
      }
      
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`staff-call-overlay ${isAnimating ? 'open' : ''}`} onClick={onClose}>
      <div 
        ref={bottomSheetRef}
        className={`staff-call-sheet ${isAnimating ? 'open' : ''} ${isDragging ? 'dragging' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          ref={wrapperRef}
          className="staff-call-wrapper"
          style={{ 
            transform: `translateY(${currentY}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isDragging && currentY > 50 ? 0.95 : 1
          }}
        >
        {/* 드래그 핸들 영역 */}
        <div 
          className="drag-handle-area"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="drag-handle" />
        </div>

        {/* 헤더 섹션 */}
        <div className="header-section">
          <img 
            src={bellIcon}
            alt="호출벨" 
            className="bell-icon"
          />
          <h2 className="header-title">{t.headerTitle}</h2>
        </div>

        {/* 옵션 리스트 컨테이너 */}
        <div className="options-container">
          <div className="options-list" ref={optionsListRef}>
            {isLoadingReasons ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">로딩 중...</p>
              </div>
            ) : callOptions.length === 0 ? (
              <div className="empty-container">
                <p className="empty-text">호출 사유가 없습니다.</p>
              </div>
            ) : (
              callOptions.map(option => (
            <button
              key={option.id}
              className={`option-item ${selectedItems.includes(option.id) ? 'selected' : ''}`}
              onClick={() => toggleItem(option.id)}
            >
              <span className="option-text">{option.label}</span>
              <div className="staff-check-icon-wrapper">
                <svg
                  className="staff-check-icon"
                  width="20"
                  height="15"
                  viewBox="0 0 20 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M19.5606 0.462093C19.8419 0.757724 20 1.15863 20 1.57665C20 1.99468 19.8419 2.39558 19.5606 2.69121L8.31146 14.5073C8.1628 14.6635 7.9863 14.7874 7.79205 14.872C7.5978 14.9565 7.3896 15 7.17934 15C6.96908 15 6.76088 14.9565 6.56663 14.872C6.37238 14.7874 6.19588 14.6635 6.04722 14.5073L0.458182 8.63764C0.314838 8.49222 0.200503 8.31826 0.121847 8.12593C0.0431903 7.93359 0.00178833 7.72673 5.66658e-05 7.51741C-0.001675 7.30808 0.0362984 7.1005 0.111761 6.90675C0.187223 6.71301 0.298664 6.537 0.439579 6.38898C0.580495 6.24096 0.748064 6.1239 0.932509 6.04464C1.11695 5.96537 1.31458 5.92548 1.51386 5.9273C1.71314 5.92912 1.91007 5.97261 2.09318 6.05523C2.27628 6.13785 2.44189 6.25795 2.58034 6.40852L7.17884 11.2388L17.4374 0.462093C17.5768 0.315598 17.7423 0.199386 17.9244 0.120098C18.1066 0.0408099 18.3018 0 18.499 0C18.6962 0 18.8914 0.0408099 19.0736 0.120098C19.2557 0.199386 19.4212 0.315598 19.5606 0.462093Z"
                    fill="#FDCD27"
                  />
                </svg>
              </div>
            </button>
            )))}
          </div>
          
          {/* 커스텀 스크롤바 */}
          <div className="custom-scrollbar-track">
            <div 
              ref={scrollbarRef}
              className="custom-scrollbar-thumb"
              style={{ 
                top: `${scrollbarTop}px`,
                height: `${scrollbarHeight}px`
              }}
              onMouseDown={handleScrollbarMouseDown}
              onTouchStart={handleScrollbarMouseDown}
            />
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="bottom-actions">
          <button className="action-button close-button" onClick={onClose}>
            {t.close}
          </button>
          <button 
            className={`action-button call-button ${selectedItems.length > 0 ? 'active' : ''}`}
            onClick={handleCall}
            disabled={selectedItems.length === 0}
          >
            {t.call}
          </button>
        </div>
        </div>
      </div>

      <style jsx="true">{`
        .staff-call-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0);
          z-index: 1001;
          transition: background 0.2s ease;
          overscroll-behavior: contain;
          touch-action: pan-y;
        }

        .staff-call-overlay.open {
          background: rgba(0, 0, 0, 0.5);
        }

        .staff-call-sheet {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          transform: translateY(100%);
          width: 100%;
          height: 85%;
          max-height: 85%;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .staff-call-sheet.open {
          transform: translateY(0);
        }
        
        .staff-call-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          background: white;
          border-radius: 24px 24px 0 0;
          overflow: hidden;
          overscroll-behavior: none;
          touch-action: none;
        }
        
        @media screen and (min-width: 361px) {
          .staff-call-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .staff-call-wrapper {
            zoom: 1.5;
          }
        }
        
        @media screen and (max-width: 360px) {
          .staff-call-wrapper {
            width: 100vw;
            zoom: calc(100vw / 360);
          }
        }

        .drag-handle-area {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 48px; /* 더 넓은 터치 영역 */
          cursor: grab;
          touch-action: none;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          z-index: 1;
          padding-top: 12px;
          box-sizing: border-box;
        }

        .drag-handle-area:active {
          cursor: grabbing;
        }

        .drag-handle {
          width: 52px;
          height: 4px;
          background: #D1D5D9;
          border-radius: 10px;
          transition: all 0.2s ease;
          margin-top: 8px;
        }
        
        .staff-call-sheet.open .drag-handle {
          background: #E5E8EB;
        }
        
        /* 드래그 중일 때 핸들 강조 */
        .drag-handle-area:active .drag-handle {
          background: #C9CDD1;
          transform: scaleX(1.2);
        }

        .header-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          border-bottom: 1px solid #F3F5F7;
          padding-bottom: 26px;
          padding-top: 40px; /* 12px(handle margin) + 4px(handle height) + 12px(padding) + 18px(gap) */
        }

        .bell-icon {
          width: 42px;
          height: 42px;
          margin-bottom: 4px;
        }

        .header-title {
          font-size: 18px;
          font-weight: 600;
          color: #2E2E2E;
          margin: 0;
          text-align: center;
        }

        .options-container {
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .options-list {
          height: 100%;
          padding: 0px 0 100px 0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        
        .options-list::-webkit-scrollbar {
          display: none;
        }
        
        /* 커스텀 스크롤바 스타일 */
        .custom-scrollbar-track {
          position: absolute;
          right: 0px;
          top: 3px;
          bottom: 100px;
          width: 6px;
          z-index: 100;
        }
        
        .custom-scrollbar-thumb {
          position: absolute;
          right: 0;
          width: 6px;
          background:rgb(201, 201, 201);
          border-radius: 3px;
          cursor: grab;
          transition: background 0.2s ease;
        }
        
        .custom-scrollbar-thumb:hover {
          
        }
        
        .custom-scrollbar-thumb:active {
          cursor: grabbing;
          
        }

        .option-item {
          width: 100%;
          height: 61px;
          margin: 0;
          padding: 20px 32px;
          background: white;
          border: none;
          border-top: 1px solid #F3F5F7;
          border-bottom: 1px solid #F3F5F7;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
          box-sizing: border-box;
        }
        
        .option-item + .option-item {
          margin-top: -1px;
        }

        .option-text {
          font-size: 18px;
          font-weight: 600;
          color: #BFBFBF;
          text-align: left;
        }

        .option-item.selected .option-text {
          color: #2E2E2E;
          transition: color 0.1s ease;
        }

        .staff-check-icon-wrapper {
          position: absolute;
          right: 32px;
          width: 20px;
          height: 15px;
          overflow: hidden;
        }

        .staff-check-icon {
          opacity: 0;
        }

        .option-item.selected .staff-check-icon {
          opacity: 1;
          animation: checkDraw 0.15s ease-out forwards;
        }
        
        @keyframes checkDraw {
          0% {
            clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
          }
          100% {
            clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
          }
        }

        .bottom-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100px;
          background-image: url(${bottomNavBg});
          background-size: cover;
          background-position: center;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 16px;
          gap: 8px;
        }

        .action-button {
          width: 160px;
          height: 50px;
          border-radius: 14px;
          border: none;
          font-size: 17px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .close-button {
          background: #F1F1F1;
          color: #949494;
        }

        .close-button:active {
          animation: buttonPress 0.15s ease-out;
        }

        .call-button {
          background: #FDCD27;
          color: #FFFFFF;
          opacity: 0.5;
        }

        .call-button.active {
          background: #FDCD27;
          color: #FFFFFF;
          opacity: 1;
        }

        .call-button:active:not(:disabled) {
          animation: buttonPress 0.15s ease-out;
        }
        
        @keyframes buttonPress {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(0.94);
          }
          100% {
            transform: scale(1);
          }
        }

        .call-button:disabled {
          cursor: not-allowed;
        }

        /* 로딩 및 빈 상태 스타일 */
        .loading-container, .empty-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #666;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f0f0f0;
          border-top: 3px solid #333;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-text, .empty-text {
          margin-top: 16px;
          font-size: 14px;
          color: #666;
        }

        /* OKDC 미지원 메시지 */
        .okdc-not-supported {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 85%;
          padding: 40px 20px;
        }

        .okdc-message {
          font-size: 16px;
          font-weight: 500;
          color: #C3C3C3;
          text-align: center;
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
};

export default StaffCallBottomSheet;